import React, { useEffect, useMemo } from "react";
import { Drawer, Box, Typography, Divider } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
  updateViewerOptions,
} from "@store/slices/chart-config-slice";
import type { VisualizationBase } from "@models/VisualizationBase";
import { isOnlyBaseNameChanged } from "@utils/visualization-base-utils";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import { dataAvailabilityPreviewSelector } from "@store/slices/data-availability-preview-slice";
import { getEffectiveAvailabilityDate } from "@utils/data-availability-preview-utils";
import {
  useGetDatasetByIdQuery,
  useGetWeightConfigurationsQuery,
} from "@store/api/dataset-api";
import {
  buildDefaultDateGuardrails,
  type GuardrailRollingOption,
  type ViewerSelectableTimePeriod,
  type PointInTimeViewerOptions,
} from "@models/Dataset";
import { getPointInTimeUnitForPeriod } from "@utils/time-period-utils";
// Reuse the bar chart's shared section components and logic hooks — stacked
// bar shares the same chart-config-slice, so weight/base/time-period/stat
// testing/save logic is identical. Top N, Combine Remaining Into Other and
// Group By are out of scope for stacked bar and are intentionally omitted.
import DrawerHeader from "../bar-chart-config/components/DrawerHeader";
import OrientationSection from "../bar-chart-config/components/OrientationSection";
import TimePeriodSection from "../bar-chart-config/components/TimePeriodSection";
import StatisticalTestingSection from "../bar-chart-config/components/StatisticalTestingSection";
import DrawerFooter from "../bar-chart-config/components/DrawerFooter";
import BaseSection from "@components/charts/common/base-config/BaseSection";
import PointInTimeViewerOptionsSection from "../PointInTimeViewerOptionsSection";
import { useBarChartHandlers } from "../bar-chart-config/hooks/useBarChartHandlers";
import { useBarChartDerivedState } from "../bar-chart-config/hooks/useBarChartDerivedState";
import { useVisualizationBase } from "../bar-chart-config/hooks/useVisualizationBase";
import { useStackedBarChartData } from "./hooks/useStackedBarChartData";
import StackedFormattingSection from "./components/StackedFormattingSection";
import StackedCategorySection from "./components/StackedCategorySection";
import StackedStylingSection from "./components/StackedStylingSection";
import StackedBarPreviewPanel from "./components/StackedBarPreviewPanel";
import {
  getStackedVisibleCounts,
  resolveStackedSegmentColorEntries,
  processStackedBarChartData,
} from "@utils/stacked-bar-utils";

const StackedBarChartConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetName, activeFilters, datasetId } =
    useAppSelector(chartConfigSelector);
  const { isDrawerOpen, editingWidgetId, dashboardId } =
    useAppSelector(chartDrawerSelector);
  const dataAvailabilityPreview = useAppSelector(dataAvailabilityPreviewSelector);

  const { data: datasetDetails } = useGetDatasetByIdQuery(datasetId as string, {
    skip: !datasetId,
  });
  const effectiveAvailabilityDate = getEffectiveAvailabilityDate(
    datasetDetails?.dataAvailabilityDate,
    dataAvailabilityPreview,
    dashboardId,
    datasetId,
  );

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  // ─── Point-in-Time Date Guardrails (same as bar chart) ────────────────────
  const pitGuardrails = useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.pointInTime ??
      buildDefaultDateGuardrails().pointInTime
    );
  }, [datasetDetails?.dateGuardrails]);

  const currentPitUnit = useMemo(
    () => getPointInTimeUnitForPeriod(currentConfig.timePeriodSelection?.timePeriod ?? ""),
    [currentConfig.timePeriodSelection?.timePeriod],
  );

  const pitUnitGuardrail = currentPitUnit
    ? pitGuardrails.units[currentPitUnit]
    : null;

  const pitGuardrailAllowedWindowSizes: number[] | undefined =
    pitUnitGuardrail?.allowedRollingAverages.filter((n) => n !== 0);

  const pitGuardrailNoRollingDisabled: boolean = pitUnitGuardrail
    ? !pitUnitGuardrail.allowedRollingAverages.includes(
        0 as GuardrailRollingOption,
      )
    : false;

  const pitGuardrailRollingDisabled: boolean =
    !!pitUnitGuardrail && (pitGuardrailAllowedWindowSizes?.length ?? 0) === 0;

  const pitGuardrailConflict = useMemo((): string | null => {
    if (!pitUnitGuardrail || !currentPitUnit) return null;
    const isRollingEnabled = currentConfig.rollingAverage?.enabled ?? false;
    const windowSize = (currentConfig.rollingAverage?.windowSize ??
      0) as GuardrailRollingOption;
    const effectiveOption: GuardrailRollingOption = isRollingEnabled
      ? windowSize
      : 0;
    if (!pitUnitGuardrail.allowedRollingAverages.includes(effectiveOption)) {
      return isRollingEnabled
        ? `Rolling average of ${windowSize} periods is no longer allowed for ${currentPitUnit} by dataset policy.`
        : `No Rolling Average is no longer allowed for ${currentPitUnit} by dataset policy.`;
    }
    return null;
  }, [pitUnitGuardrail, currentPitUnit, currentConfig.rollingAverage]);

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateOrientation,
    updateValuePlacement,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateChartTitle,
    updateChartSubtitle,
    setStackedColorPalette,
    setStackedSegmentColor,
    setStackedChartData,
    updateChartData,
  } = chartConfigDispatcher(dispatch);

  const { fetchChartData, isRefetchingData, weightsApplied, setWeightsApplied } =
    useStackedBarChartData();

  const {
    localApplyFilters,
    isTogglingFilters,
    debouncedApplyFilters,
    usePreviewDateFilter,
    handleFilterToggle,
    handlePreviewDateFilterToggle,
    handleWeightChange,
    handleTimePeriodChange,
    handleTimePeriodDatesChange,
    handleStatTestingChange,
    handleRollingAverageEnabledChange,
    handleRollingAverageWindowSizeChange,
    handleSave,
    handleDiscard,
    isCreatingChart,
  } = useBarChartHandlers({
    fetchChartData,
    isRefetchingData,
    setWeightsApplied,
    pitGuardrails,
    apiChartType: "STACKED_BAR",
    processStatTestingResponse: (resultData) => {
      const stacked = processStackedBarChartData(resultData.data);
      setStackedChartData(stacked);
      updateChartData({
        categories: stacked.categories.map((c) => c.label),
        series: stacked.series.map((s) => ({
          name: s.label,
          variableId: s.id,
          data: s.data.map((cell) => cell.value),
        })),
        responsesMinDate: resultData.responsesMinDate,
        responsesMaxDate: resultData.responsesMaxDate,
        effectiveRollingPeriodCount: resultData.effectiveRollingPeriodCount,
        rollingPeriodsWarningMessage: resultData.rollingPeriodsWarningMessage,
        comparisonRollingLabel: resultData.comparisonRollingLabel,
        missingRollingPeriodLabels: resultData.missingRollingPeriodLabels,
      });
    },
  });

  useEffect(() => {
    if (editingWidgetId !== "new-widget") return;
    // Stacked bar defaults: Horizontal orientation, values shown inside each
    // segment — different from Bar/Grouped Bar's vertical/end-of-bar default.
    if (currentConfig.orientation !== "horizontal") {
      updateOrientation("horizontal");
    }
    if (currentConfig.valuePlacement !== "inside") {
      updateValuePlacement("inside");
    }
    if (
      (weightConfigData?.weightConfigurations?.length ?? 0) > 0 &&
      currentConfig.weight === "DISABLED"
    ) {
      handleWeightChange("DATASET_DEFAULT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingWidgetId, weightConfigData]);

  const {
    questions,
    isAvailableData,
    canSave,
    headerTitle,
    isAllTimePeriod,
  } = useBarChartDerivedState(isRefetchingData, effectiveAvailabilityDate);

  const isDisabled = isRefetchingData || isTogglingFilters;

  const {
    base: visualizationBase,
    isValid: isVisualizationBaseValid,
    setBase: setVisualizationBase,
  } = useVisualizationBase();

  // Require at least one visible Stack (Segments are always fully included).
  const isStackedSelectionValid = useMemo(() => {
    const { stacks } = getStackedVisibleCounts(currentConfig.stackedBar);
    return stacks >= 1;
  }, [currentConfig.stackedBar]);

  // Segment entries (with display labels) for the Custom-palette swatches.
  const segmentColorEntries = useMemo(
    () => resolveStackedSegmentColorEntries(currentConfig.stackedBar),
    [currentConfig.stackedBar],
  );

  // ─── Base changes refetch immediately (same pattern as bar chart) ─────────
  const handleVisualizationBaseChange = (updatedBase: VisualizationBase) => {
    setVisualizationBase(updatedBase);
    // Renaming a Custom Base is a cosmetic label change with no effect on the
    // resolved filters — skip the refetch so it doesn't fire on every keystroke.
    if (isOnlyBaseNameChanged(currentConfig.visualizationBase, updatedBase)) return;
    fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, undefined, undefined, undefined, updatedBase);
  };

  const isViewerOptionsInvalid =
    currentPitUnit !== null &&
    currentConfig.viewerOptions.enabled &&
    Object.keys(currentConfig.viewerOptions.additionalPeriods).length === 0 &&
    currentConfig.viewerOptions.chartPeriodAdditionalRollingAverages.length ===
      0;

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => closeChartDrawer()}
      sx={{ "& .MuiDrawer-paper": { width: "80vw", boxSizing: "border-box" } }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <DrawerHeader
          questionLabel={questions?.label}
          headerTitle={headerTitle}
          datasetName={datasetName}
          activeFiltersCount={activeFilters.length}
          localApplyFilters={localApplyFilters}
          isTogglingFilters={isTogglingFilters}
          isRefetchingData={isRefetchingData}
          onFilterToggle={handleFilterToggle}
          usePreviewDateFilter={usePreviewDateFilter}
          onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
        />

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Configuration Panel */}
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              Configurations
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <StackedCategorySection isDisabled={isDisabled} />

              <OrientationSection
                orientation={currentConfig.orientation}
                onOrientationChange={updateOrientation}
              />

              <StackedFormattingSection
                percentageFormatting={currentConfig.percentageFormatting}
                valuePlacement={
                  currentConfig.valuePlacement === "inside" ? "inside" : "none"
                }
                weight={currentConfig.weight}
                weightsApplied={weightsApplied}
                isRefetchingData={isRefetchingData}
                onPercentageFormattingChange={
                  chartConfigDispatcher(dispatch).updatePercentageFormatting
                }
                onValuePlacementChange={updateValuePlacement}
                onWeightChange={handleWeightChange}
              />

              <Divider />

              <BaseSection
                datasetId={datasetId}
                isDisabled={isDisabled}
                base={visualizationBase}
                onChange={handleVisualizationBaseChange}
              />

              <Divider />

              <TimePeriodSection
                timePeriodSelection={currentConfig.timePeriodSelection}
                rollingAverage={currentConfig.rollingAverage}
                statisticalTestingEnabled={
                  currentConfig.statisticalTesting?.enabled || false
                }
                responsesMinDate={currentConfig.responsesMinDate}
                responsesMaxDate={currentConfig.responsesMaxDate}
                datasetId={datasetId}
                isDisabled={isDisabled}
                totalAvailablePeriods={currentConfig.categories?.length}
                onTimePeriodChange={handleTimePeriodChange}
                onTimePeriodDatesChange={handleTimePeriodDatesChange}
                onRollingAverageEnabledChange={handleRollingAverageEnabledChange}
                onRollingAverageWindowSizeChange={
                  handleRollingAverageWindowSizeChange
                }
                guardrailAllowedWindowSizes={pitGuardrailAllowedWindowSizes}
                guardrailNoRollingDisabled={pitGuardrailNoRollingDisabled}
                guardrailRollingDisabled={pitGuardrailRollingDisabled}
                guardrailConflictWarning={pitGuardrailConflict ?? undefined}
              />

              {currentPitUnit && (
                <>
                  <Divider />
                  <PointInTimeViewerOptionsSection
                    viewerOptions={currentConfig.viewerOptions}
                    chartTimePeriod={
                      currentConfig.timePeriodSelection
                        ?.timePeriod as ViewerSelectableTimePeriod
                    }
                    chartRollingWindowSize={
                      currentConfig.rollingAverage?.enabled
                        ? (currentConfig.rollingAverage?.windowSize ?? 0)
                        : 0
                    }
                    pitGuardrailUnits={pitGuardrails.units}
                    isDisabled={isDisabled}
                    onViewerOptionsChange={(opts: PointInTimeViewerOptions) =>
                      dispatch(updateViewerOptions(opts))
                    }
                    datasetId={datasetId}
                    onRestoreSharedTime={(config) => {
                      const d = chartConfigDispatcher(dispatch);
                      d.updateTimePeriod(
                        config.savedDefault as typeof currentConfig.timePeriodSelection.timePeriod,
                      );
                      const ra = config.savedRollingAverage;
                      d.updateRollingAverageEnabled(ra > 0);
                      if (ra > 0) d.updateRollingAverageWindowSize(ra);
                      if (config.viewerOptions) {
                        dispatch(
                          updateViewerOptions(
                            config.viewerOptions as typeof currentConfig.viewerOptions,
                          ),
                        );
                      }
                    }}
                  />
                </>
              )}

              <Divider />

              <StatisticalTestingSection
                statisticalTesting={currentConfig.statisticalTesting}
                isDisabled={isDisabled}
                isAllTimePeriod={isAllTimePeriod}
                rollingAverageEnabled={
                  currentConfig.rollingAverage?.enabled || false
                }
                onStatTestingChange={handleStatTestingChange}
                updateStatTestingEnabled={updateStatTestingEnabled}
                updateStatTestType={updateStatTestType}
                updateSignificanceLevel={updateSignificanceLevel}
                updateMinimumSampleSize={updateMinimumSampleSize}
              />

              <Divider />

              <StackedStylingSection
                title={currentConfig.title}
                subtitle={currentConfig.subtitle}
                colorPalette={currentConfig.stackedBar?.colorPalette ?? "Multicolor"}
                segmentColorEntries={segmentColorEntries}
                manualColors={currentConfig.stackedBar?.segmentManualColors ?? {}}
                onTitleChange={updateChartTitle}
                onSubtitleChange={updateChartSubtitle}
                onColorPaletteChange={setStackedColorPalette}
                onSegmentColorChange={(id, color) =>
                  setStackedSegmentColor({ id, color })
                }
              />
            </Box>
          </Box>

          {/* Preview Panel */}
          <StackedBarPreviewPanel
            currentConfig={currentConfig}
            datasetId={datasetId}
            isRefetchingData={isRefetchingData}
            isAvailableData={isAvailableData}
          />
        </Box>

        <DrawerFooter
          isCreatingChart={isCreatingChart}
          canSave={
            canSave &&
            !pitGuardrailConflict &&
            !isViewerOptionsInvalid &&
            isVisualizationBaseValid &&
            isStackedSelectionValid
          }
          isRefetchingData={isRefetchingData}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </Box>
    </Drawer>
  );
};

export default StackedBarChartConfigDrawer;
